import React, { useState, useMemo, useEffect } from "react";
import { Card, Row, Col, Button, Badge, Table, Dropdown, Modal, Form, Alert, Spinner, Toast, ToastContainer } from "react-bootstrap";
import '../assets/css/sidebar.css';
import { useAuth } from "../hooks/useAuth";
import Footer from "../layouts/Footer";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Création du client API
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default function MonCompteOrganisation() {
  const [activeTab, setActiveTab] = useState("organisation");
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [showEditBillingModal, setShowEditBillingModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [email, setEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [membresData, setMembresData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validatingUser, setValidatingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [validationMessage, setValidationMessage] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  // États pour l'ajout de membre
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({
    firstname: '',
    lastname: '',
    business_name: '',
    business_country: '',
    business_city: '',
    business_email: '',
    phone: '',
    country_code: 'GA'
  });
  
  // État pour le formulaire d'édition de membre
  const [editMemberForm, setEditMemberForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    status: 'OK'
  });

  // État pour le formulaire d'édition de l'organisation
  const [editOrgForm, setEditOrgForm] = useState({
    business_name: '',
    business_email: '',
    phone: '',
    business_country: '',
    business_city: '',
    business_id: ''
  });
  
  const { user, business, logout } = useAuth();

  // Fonction pour récupérer les membres via l'API
  const fetchMembers = async () => {
    if (!user?.business_id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/users');
      
      if (response.status === 200 && Array.isArray(response.data.data)) {
        const formattedMembers = formatMembersData(response.data.data);
        setMembresData(formattedMembers);
      } else {
        throw new Error(`Réponse API invalide: ${response.data.message || 'Structure de données incorrecte'}`);
      }
      
    } catch (err) {
      console.error('Erreur lors de la récupération des membres:', err);
      setError(`Impossible de charger les membres: ${err.message}`);
      setMembresData(getDemoData());
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ouvrir le modal d'édition de l'organisation
  const openEditOrgModal = () => {
    setEditOrgForm({
      business_name: business?.business_name || '',
      business_email: business?.business_email || '',
      phone: business?.phone || '',
      business_country: business?.business_country || '',
      business_city: business?.business_city || '',
      business_id: business?.business_id || ''
    });
    setShowEditOrgModal(true);
  };

  // Fonction pour mettre à jour l'entreprise via l'API
  const updateOrganization = async (e) => {
    if (e) e.preventDefault();
    
    if (!business?.business_id) {
      setToastMessage("Erreur: ID entreprise non disponible");
      setShowErrorToast(true);
      return;
    }
    
    setLoading(true);
    
    try {
      const updateData = {
        business_name: editOrgForm.business_name.trim() || null,
        business_email: editOrgForm.business_email.trim() || null,
        phone: editOrgForm.phone.trim() || null,
        business_country: editOrgForm.business_country.trim() || null,
        business_city: editOrgForm.business_city.trim() || null
      };

      const response = await apiClient.put(`/business/${business.business_id}`, updateData);

      if (response.status === 200) {
        setToastMessage("Informations de l'entreprise mises à jour avec succès !");
        setShowSuccessToast(true);
        setShowEditOrgModal(false);
      } else {
        throw new Error(response.data.message || 'Erreur lors de la mise à jour de l\'entreprise');
      }
      
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour de l\'entreprise:', err);
      setToastMessage(`Erreur lors de la mise à jour: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ouvrir le modal d'édition de membre
  const openEditMemberModal = (member) => {
    setSelectedMember(member);
    setEditMemberForm({
      first_name: member.firstname || member.nomComplet?.split(' ')[0] || '',
      last_name: member.lastname || member.nomComplet?.split(' ').slice(1).join(' ') || '',
      email: member.email || '',
      phone: member.phone || '',
      status: member.status === 'OK' || member.account_validation === 'OK' ? 'OK' : 'KO'
    });
    setShowEditMemberModal(true);
  };

  // Fonction pour mettre à jour un membre via l'API
  const updateMember = async (e) => {
    if (e) e.preventDefault();
    
    if (!selectedMember) return;
    
    setUpdatingUser(selectedMember.id);
    
    try {
      const updateData = {
        firstname: editMemberForm.first_name.trim() || null,
        lastname: editMemberForm.last_name.trim() || null,
        email: editMemberForm.email.trim() || null,
        phone: editMemberForm.phone.trim() || null,
        status: editMemberForm.status
      };

      const response = await apiClient.put(`/users/${selectedMember.id}`, updateData);

      if (response.status === 200) {
        setMembresData(prev => prev.map(member => 
          member.id === selectedMember.id 
            ? { 
                ...member,
                firstname: editMemberForm.first_name,
                lastname: editMemberForm.last_name,
                email: editMemberForm.email,
                phone: editMemberForm.phone,
                status: editMemberForm.status,
                account_validation: editMemberForm.status,
                nomComplet: `${editMemberForm.first_name} ${editMemberForm.last_name}`.trim(),
                statut: getMemberStatus({ 
                  ...member, 
                  status: editMemberForm.status,
                  account_validation: editMemberForm.status 
                })
              } 
            : member
        ));

        setToastMessage(`Membre ${editMemberForm.email} mis à jour avec succès !`);
        setShowSuccessToast(true);
        setShowEditMemberModal(false);
        setSelectedMember(null);
        await fetchMembers();
        
      } else {
        throw new Error(response.data.message || 'Erreur lors de la mise à jour');
      }
      
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour du membre:', err);
      setToastMessage(`Erreur lors de la mise à jour: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setUpdatingUser(null);
    }
  };

  // Fonction pour ajouter un nouveau membre
  const addNewMember = async (e) => {
    if (e) e.preventDefault();
    
    setAddingMember(true);
    
    try {
      if (!addMemberForm.business_email || !addMemberForm.firstname || !addMemberForm.lastname) {
        throw new Error('Les champs email, prénom et nom sont obligatoires');
      }

      const response = await apiClient.post('/users', addMemberForm);

      await fetchMembers();
      
      setToastMessage(`Membre ${addMemberForm.business_email} ajouté avec succès !`);
      setShowSuccessToast(true);
      
      setShowAddMemberModal(false);
      setAddMemberForm({
        firstname: '',
        lastname: '',
        business_name: '',
        business_country: '',
        business_city: '',
        business_email: '',
        phone: '',
        country_code: 'GA'
      });
      
    } catch (err) {
      console.error('❌ Erreur lors de l\'ajout du membre:', err);
      setToastMessage(`Erreur lors de l'ajout: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setAddingMember(false);
    }
  };

  // Fonction pour réinitialiser le formulaire d'ajout
  const resetAddMemberForm = () => {
    setAddMemberForm({
      firstname: '',
      lastname: '',
      business_name: '',
      business_country: '',
      business_city: '',
      business_email: '',
      phone: '',
      country_code: 'GA'
    });
  };

  // Fonction pour ouvrir le modal d'ajout
  const openAddMemberModal = () => {
    resetAddMemberForm();
    setShowAddMemberModal(true);
  };

  // Fonction pour ouvrir le modal de validation
  const openValidationModal = (member) => {
    setSelectedMember(member);
    const currentStatus = member.status || member.account_validation;
    const action = currentStatus === 'OK' ? 'désactiver' : 'activer';
    setValidationMessage(`Êtes-vous sûr de vouloir ${action} le compte de ${member.email} ?`);
    setShowValidationModal(true);
  };

  // Fonction pour ouvrir le modal de suppression
  const openDeleteModal = (member) => {
    setSelectedMember(member);
    setDeleteMessage(`Êtes-vous sûr de vouloir supprimer définitivement le compte de ${member.email} ? Cette action est irréversible.`);
    setShowDeleteModal(true);
  };

  // Fonction pour valider un utilisateur
  const validateUser = async () => {
    if (!selectedMember) return;
    
    const currentStatus = selectedMember.status || selectedMember.account_validation;
    const newStatus = currentStatus === 'OK' ? 'KO' : 'OK';
    
    setValidatingUser(selectedMember.id);
    setShowValidationModal(false);
    
    try {
      const response = await apiClient.put(`/users/${selectedMember.id}`, {
        status: newStatus
      });
      
      if (response.status === 200) {
        setMembresData(prev => prev.map(member => 
          member.id === selectedMember.id 
            ? { 
                ...member, 
                account_validation: newStatus,
                status: newStatus,
                statut: getMemberStatus({ ...member, account_validation: newStatus, status: newStatus })
              } 
            : member
        ));
        
        const action = newStatus === 'OK' ? 'activé' : 'désactivé';
        setToastMessage(`Compte ${action} avec succès !`);
        setShowSuccessToast(true);
        await fetchMembers();
        
      } else {
        throw new Error(response.data.message || 'Erreur lors de la validation');
      }
      
    } catch (err) {
      console.error('Erreur lors de la validation:', err);
      setToastMessage(`Erreur: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setValidatingUser(null);
      setSelectedMember(null);
    }
  };

  // Fonction pour supprimer un utilisateur
  const deleteUser = async () => {
    if (!selectedMember) return;
    
    setDeletingUser(selectedMember.id);
    setShowDeleteModal(false);
    
    try {
      const response = await apiClient.delete(`/users/${selectedMember.id}`);

      if (response.status === 200) {
        setMembresData(prev => prev.filter(member => member.id !== selectedMember.id));
        setToastMessage(`Membre ${selectedMember.email} supprimé avec succès !`);
        setShowSuccessToast(true);
        await fetchMembers();
        
      } else {
        throw new Error(response.data.message || 'Erreur lors de la suppression');
      }
      
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setToastMessage(`Erreur lors de la suppression: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setDeletingUser(null);
      setSelectedMember(null);
    }
  };

  // Fonction pour formater les données de l'API
  const formatMembersData = (apiDataArray) => {
    if (!Array.isArray(apiDataArray)) {
      return getDemoData();
    }
    
    return apiDataArray.map((member, index) => ({
      id: member.id || index + 1,
      email: member.business_email || member.email || `membre${index + 1}@minerve-tech.com`,
      statut: getMemberStatus(member),
      role: mapRole(member.acc_type, member.is_system),
      derniereConnexion: formatLastLogin(member.updated_at),
      nomComplet: `${member.firstname || ''} ${member.lastname || ''}`.trim(),
      firstname: member.firstname,
      lastname: member.lastname,
      phone: member.phone,
      business_name: member.business_name,
      kyc: member.kyc,
      account_validation: member.account_validation,
      status: member.status,
      actions: ["Modifier les informations", "Supprimer"],
      isCurrentUser: member.id === user?.id
    }));
  };

  // Déterminer le statut du membre
  const getMemberStatus = (memberData) => {
    if (memberData.account_validation === 'OK' && memberData.kyc === 'OK') {
      return "ACTIF";
    }
    return "INACTIF";
  };

  // Fonction pour mapper les rôles
  const mapRole = (accType, isSystem) => {
    if (isSystem === 1) return "Système";
    
    const roleMap = {
      'business': 'Administrateur',
      'admin': 'Administrateur',
      'technician': 'Technicien',
      'commercial': 'Commercial',
      'user': 'Utilisateur'
    };
    return roleMap[accType] || 'Utilisateur';
  };

  // Fonction pour formater la dernière connexion
  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return "Non mentioné";
    
    try {
      const date = new Date(lastLogin);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return "Date invalide";
    }
  };

  // Données de démonstration (fallback)
  const getDemoData = () => [
    // Données de démonstration si nécessaire
  ];

  // Charger les membres au montage du composant
  useEffect(() => {
    fetchMembers();
  }, [business?.business_id]);

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString || dateTimeString === '0000-00-00 00:00:00') {
      return 'Non définie';
    }
    
    try {
      const date = new Date(dateTimeString);
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(',', '');
      
    } catch (error) {
      console.error('Erreur de formatage date/heure:', error);
      return 'Erreur date';
    }
  };

  const organisationData = {
    nom: `${business?.business_name}`,
    verification: user?.kyc === 'OK' ? 100 : 60,
    dateCreation: `${formatDateTime(user?.reg_date)}`,
    telephone: `${business?.phone}`,
    siren: `${business?.business_id || user?.id}`,
    email: `${business?.business_email}`,
    responsable: `${user?.firstname} ${user?.lastname}`
  };

  const facturationData = {
    adresse: [
      business?.business_name || '',
      business?.business_city || 'Ville non définie',
      business?.business_country || 'Pays non défini',
      business?.business_id ? `SIREN: ${business.business_id}` : "ND"
    ],
    email: business?.business_email
  };

  // Filtrer les membres basé sur la recherche
  const filteredMembres = useMemo(() => {
    if (!searchTerm.trim()) return membresData;
    
    const term = searchTerm.toLowerCase();
    return membresData.filter(membre => 
      membre.email.toLowerCase().includes(term) ||
      membre.role.toLowerCase().includes(term) ||
      membre.statut.toLowerCase().includes(term) ||
      membre.derniereConnexion.toLowerCase().includes(term) ||
      (membre.nomComplet && membre.nomComplet.toLowerCase().includes(term)) ||
      (membre.business_name && membre.business_name.toLowerCase().includes(term))
    );
  }, [membresData, searchTerm]);

  // Compter les membres actifs
  const activeMembersCount = useMemo(() => {
    return filteredMembres.filter(membre => membre.account_validation === "OK").length;
  }, [filteredMembres]);

  const handleAddMember = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const role = formData.get('role');
    
    const newMember = {
      id: Date.now(),
      email: email,
      statut: "INACTIF",
      role: role,
      derniereConnexion: "Invitation envoyée",
      nomComplet: "",
      phone: "",
      business_name: "",
      kyc: "PENDING",
      account_validation: "PENDING",
      status: "PENDING",
      actions: ["Modifier les informations", "Supprimer"],
      isCurrentUser: false
    };
    
    setMembresData(prev => [...prev, newMember]);
    setShowAddMemberModal(false);
  };

  const handleEditOrganisation = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const business_name = formData.get('business_name');
    const business_email = formData.get('business_email');
    const phone = formData.get('phone');
    const business_id = formData.get('business_id');
    const responsable = formData.get('responsable');
    
    setShowEditOrgModal(false);
  };

  // Gestionnaire de recherche
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Réinitialiser la recherche
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Recharger les membres
  const handleReloadMembers = () => {
    fetchMembers();
  };

  // Gérer les actions sur les membres
  const handleMemberAction = (memberId, action) => {
    const member = membresData.find(m => m.id === memberId);
    
    switch (action) {
      case "Modifier les informations":
        if (member) {
          openEditMemberModal(member);
        }
        break;
        
      case "Valider/Invalider":
        if (member) {
          openValidationModal(member);
        }
        break;
        
      case "Supprimer":
        if (member) {
          if (member.isCurrentUser) {
            setToastMessage("Vous ne pouvez pas supprimer votre propre compte.");
            setShowErrorToast(true);
          } else {
            openDeleteModal(member);
          }
        }
        break;
      default:
        break;
    }
  };



   return (
    <div className="mo-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS PREMIUM ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={showSuccessToast} 
          onClose={() => setShowSuccessToast(false)} 
          delay={5000} 
          autohide
          className="mo-toast mo-toast-success"
        >
          <Toast.Header>
            <div className="mo-toast-icon mo-toast-icon-success">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <strong className="me-auto">Succès</strong>
            <small>Maintenant</small>
          </Toast.Header>
          <Toast.Body>
            {toastMessage}
          </Toast.Body>
        </Toast>

        <Toast 
          show={showErrorToast} 
          onClose={() => setShowErrorToast(false)} 
          delay={5000} 
          autohide
          className="mo-toast mo-toast-error"
        >
          <Toast.Header>
            <div className="mo-toast-icon mo-toast-icon-error">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <strong className="me-auto">Erreur</strong>
            <small>Maintenant</small>
          </Toast.Header>
          <Toast.Body>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ========== HEADER PREMIUM ========== */}
      <div className="mo-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="mo-header-icon">
              <i className="bi bi-buildings-fill"></i>
            </div>
            <div>
              <h3 className="mo-header-title mb-1">Compte entreprise</h3>
              <p className="mo-header-subtitle mb-0">
                <i className="bi bi-info-circle"></i>
                Remplissez les détails de votre organisation afin d'accéder à plus de services
              </p>
            </div>
          </div>
          <div className="mo-header-badge">
            <span className={`mo-kyc-dot ${business?.kyc === 'OK' ? 'mo-kyc-dot-ok' : 'mo-kyc-dot-pending'}`}></span>
            <span>{business?.kyc === 'OK' ? 'KYC Validé' : 'KYC en attente'}</span>
          </div>
        </div>
      </div>

      {/* ========== ALERT ERROR ========== */}
      {error && (
        <div className="mo-alert mo-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <button className="mo-btn-secondary" onClick={handleReloadMembers}>
            <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
          </button>
        </div>
      )}

      <Row className="g-4">
        {/* ========== TABS LATÉRAUX PREMIUM ========== */}
        <Col lg={3}>
          <div className="mo-sidebar-card">
            <div className="mo-sidebar-header">
              <div className="mo-sidebar-avatar">
                {business?.business_name ? business.business_name.substring(0, 2).toUpperCase() : 'MT'}
              </div>
              <div className="mo-sidebar-info">
                <div className="mo-sidebar-company">{business?.business_name || 'Mon entreprise'}</div>
                <div className="mo-sidebar-id">ID: {business?.business_id || 'N/A'}</div>
              </div>
            </div>

            <div className="mo-sidebar-nav">
              {[
                { id: "organisation", label: "Entreprise", icon: "bi-building", desc: "Informations générales" },
                { id: "membres", label: "Membres", icon: "bi-people", desc: "Gérer les accès" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`mo-sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="mo-sidebar-tab-icon">
                    <i className={`bi ${tab.icon}`}></i>
                  </div>
                  <div className="mo-sidebar-tab-content">
                    <div className="mo-sidebar-tab-label">{tab.label}</div>
                    <div className="mo-sidebar-tab-desc">{tab.desc}</div>
                  </div>
                  {activeTab === tab.id && (
                    <i className="bi bi-chevron-right mo-sidebar-tab-arrow"></i>
                  )}
                </button>
              ))}
            </div>

            {/* Section vérification */}
            <div className="mo-sidebar-verification">
              <div className="mo-sidebar-verification-header">
                <i className="bi bi-shield-check"></i>
                <span>Vérification</span>
              </div>
              <div className="mo-sidebar-progress">
                <div 
                  className={`mo-sidebar-progress-bar ${business?.kyc === 'OK' ? 'mo-sidebar-progress-ok' : ''}`}
                  style={{ width: `${business?.kyc === 'OK' ? 100 : 60}%` }}
                ></div>
              </div>
              <div className="mo-sidebar-progress-label">
                {business?.kyc === 'OK' ? '100%' : '60%'} complété
              </div>
            </div>
          </div>
        </Col>

        {/* ========== CONTENU PRINCIPAL ========== */}
        <Col lg={9}>
          {/* ========== TAB ORGANISATION ========== */}
          {activeTab === "organisation" && (
            <div className="mo-content-card">
              <div className="mo-content-header">
                <div className="d-flex align-items-center gap-3">
                  <div className="mo-content-header-icon mo-content-header-icon-blue">
                    <i className="bi bi-building-fill"></i>
                  </div>
                  <div>
                    <h5 className="mo-content-title">Informations sur l'entreprise</h5>
                    <p className="mo-content-subtitle">Détails et paramètres de votre organisation</p>
                  </div>
                </div>
                <button className="mo-btn-primary" onClick={openEditOrgModal}>
                  <i className="bi bi-pencil"></i>
                  <span>Modifier</span>
                </button>
              </div>

              <div className="mo-content-body">
                <Row className="g-4">
                  {/* Card vérification */}
                  <Col md={5}>
                    <div className="mo-verification-card">
                      <div className="mo-verification-logo">
                        {business?.business_name ? business.business_name.substring(0, 2).toUpperCase() : 'MT'}
                      </div>
                      <h5 className="mo-verification-name">{organisationData.nom}</h5>
                      
                      {business?.kyc === 'OK' ? (
                        <>
                          <span className="mo-verification-badge mo-verification-badge-ok">
                            <i className="bi bi-patch-check-fill"></i>
                            Vérifiée à 100%
                          </span>
                          <div className="mo-verification-progress">
                            <div className="mo-verification-progress-bar mo-verification-progress-ok" style={{ width: '100%' }}></div>
                          </div>
                          <div className="mo-verification-info mo-verification-info-ok">
                            <i className="bi bi-check-circle-fill"></i>
                            Entreprise entièrement vérifiée
                          </div>
                        </>
                      ) : (
                        <div className="mo-verification-info mo-verification-info-warning">
                          <i className="bi bi-hourglass-split"></i>
                          Vérification en attente
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Card détails */}
                  <Col md={7}>
                    <div className="mo-details-card">
                      <div className="mo-details-header">
                        <i className="bi bi-info-circle-fill"></i>
                        <span>Détails de l'entreprise</span>
                      </div>
                      <div className="mo-details-body">
                        {[
                          { label: "Date de création", value: organisationData.dateCreation, icon: "bi-calendar" },
                          { label: "Business ID", value: business?.business_id, icon: "bi-hash", isCode: true },
                          { label: "Téléphone", value: organisationData.telephone, icon: "bi-telephone" },
                          { label: "Identifiant SIREN", value: organisationData.siren, icon: "bi-card-text", isCode: true },
                          { label: "Email", value: organisationData.email, icon: "bi-envelope" },
                          { label: "Responsable", value: organisationData.responsable, icon: "bi-person" },
                          { label: "Pays", value: business?.business_country || 'Non défini', icon: "bi-globe" },
                        ].map((item, index) => (
                          <div key={index} className="mo-detail-row">
                            <div className="mo-detail-icon">
                              <i className={`bi ${item.icon}`}></i>
                            </div>
                            <div className="mo-detail-content">
                              <div className="mo-detail-label">{item.label}</div>
                              <div className={`mo-detail-value ${item.isCode ? 'mo-code' : ''}`}>
                                {item.value || 'Non renseigné'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* ========== TAB MEMBRES ========== */}
          {activeTab === "membres" && (
            <div className="mo-content-card">
              <div className="mo-content-header">
                <div className="d-flex align-items-center gap-3">
                  <div className="mo-content-header-icon mo-content-header-icon-violet">
                    <i className="bi bi-people-fill"></i>
                  </div>
                  <div>
                    <h5 className="mo-content-title">Membres de l'entreprise</h5>
                    <p className="mo-content-subtitle">Gérez les accès et permissions des collaborateurs</p>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button 
                    className="mo-btn-secondary" 
                    onClick={handleReloadMembers}
                    disabled={loading}
                  >
                    <i className={`bi ${loading ? 'bi-arrow-repeat mo-spin' : 'bi-arrow-clockwise'}`}></i>
                    <span>Actualiser</span>
                  </button>
                  <button className="mo-btn-primary" onClick={openAddMemberModal}>
                    <i className="bi bi-person-plus-fill"></i>
                    <span>Ajouter</span>
                  </button>
                </div>
              </div>

              <div className="mo-content-body">
                {/* Stats membres */}
                <div className="mo-members-stats mb-4">
                  <div className="mo-member-stat">
                    <div className="mo-member-stat-icon mo-member-stat-icon-blue">
                      <i className="bi bi-people-fill"></i>
                    </div>
                    <div>
                      <div className="mo-member-stat-label">Total membres</div>
                      <div className="mo-member-stat-value">{filteredMembres.length}</div>
                    </div>
                  </div>
                  <div className="mo-member-stat">
                    <div className="mo-member-stat-icon mo-member-stat-icon-green">
                      <i className="bi bi-check-circle-fill"></i>
                    </div>
                    <div>
                      <div className="mo-member-stat-label">Membres actifs</div>
                      <div className="mo-member-stat-value mo-member-stat-value-green">{activeMembersCount}</div>
                    </div>
                  </div>
                  <div className="mo-member-stat-search">
                    <div className="mo-search-wrapper">
                      <i className="bi bi-search mo-search-icon"></i>
                      <input
                        type="text"
                        className="mo-search-input"
                        placeholder="Rechercher un membre..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                      {searchTerm && (
                        <button className="mo-search-clear" onClick={handleClearSearch}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Loading */}
                {loading ? (
                  <div className="mo-loading">
                    <div className="mo-loading-spinner"></div>
                    <div className="mo-loading-text">Chargement des membres...</div>
                  </div>
                ) : (
                  <div className="mo-table-wrapper">
                    <div className="table-responsive">
                      <table className="mo-table">
                        <thead>
                          <tr>
                            <th><i className="bi bi-person me-1"></i>Membre</th>
                            <th><i className="bi bi-flag me-1"></i>Statut</th>
                            <th><i className="bi bi-shield-check me-1"></i>Rôle</th>
                            <th><i className="bi bi-clock-history me-1"></i>Dernière connexion</th>
                            <th className="text-end"><i className="bi bi-gear me-1"></i>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMembres.length > 0 ? (
                            filteredMembres.map((membre) => (
                              <tr key={membre.id} className="mo-row">
                                <td>
                                  <div className="mo-member-cell">
                                    <div className="mo-member-avatar-wrapper">
                                      <div className="mo-member-avatar">
                                        {membre.email.charAt(0).toUpperCase()}
                                      </div>
                                      {membre.isCurrentUser && (
                                        <span className="mo-member-you-badge">Vous</span>
                                      )}
                                      <span className={`mo-member-status-dot ${
                                        membre.statut === "ACTIF" ? 'mo-status-dot-active' : 'mo-status-dot-inactive'
                                      }`}></span>
                                    </div>
                                    <div className="mo-member-info">
                                      <div className="mo-member-email">{membre.email}</div>
                                      {membre.nomComplet && (
                                        <div className="mo-member-name">{membre.nomComplet}</div>
                                      )}
                                      {membre.business_name && membre.business_name !== user?.business_name && (
                                        <div className="mo-member-company">
                                          <i className="bi bi-building"></i>
                                          {membre.business_name}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="mo-status-badges">
                                    <span className={`mo-status-badge ${
                                      membre.statut === "ACTIF" ? 'mo-status-active' : 'mo-status-inactive'
                                    }`}>
                                      <i className={`bi ${
                                        membre.statut === "ACTIF" ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'
                                      }`}></i>
                                      {membre.statut}
                                    </span>
                                    <span className={`mo-validation-badge ${
                                      membre.status === 'OK' || membre.account_validation === 'OK' 
                                        ? 'mo-validation-ok' : 'mo-validation-pending'
                                    }`}>
                                      {membre.status === 'OK' || membre.account_validation === 'OK' 
                                        ? 'Validé' : 'En attente'}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className={`mo-role-badge ${
                                    membre.role === "Administrateur" ? 'mo-role-admin' : 'mo-role-user'
                                  }`}>
                                    <i className="bi bi-shield-fill-check"></i>
                                    {membre.role}
                                  </span>
                                </td>
                                <td>
                                  <span className="mo-date-cell">
                                    <i className="bi bi-clock"></i>
                                    {membre.derniereConnexion}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <div className="mo-actions">
                                    <button
                                      className="mo-action-btn"
                                      disabled={validatingUser === membre.id || deletingUser === membre.id || updatingUser === membre.id}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const dropdown = e.currentTarget.nextElementSibling;
                                        dropdown.classList.toggle('show');
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        dropdown.style.position = 'fixed';
                                        dropdown.style.left = `${rect.left - 150}px`;
                                        dropdown.style.top = `${rect.bottom + 5}px`;
                                      }}
                                    >
                                      {validatingUser === membre.id || deletingUser === membre.id || updatingUser === membre.id ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        <i className="bi bi-three-dots-vertical"></i>
                                      )}
                                    </button>
                                    <div className="dropdown-menu mo-dropdown-menu" style={{ minWidth: '220px' }}>
                                      {membre.actions
                                        .filter(action => {
                                          if (action === "Supprimer" && membre.role === "Administrateur") return false;
                                          return true;
                                        })
                                        .map((action, i) => (
                                          <button 
                                            key={i}
                                            className="mo-dropdown-item"
                                            onClick={() => handleMemberAction(membre.id, action)}
                                            disabled={
                                              (membre.isCurrentUser && action === "Supprimer") || 
                                              validatingUser === membre.id ||
                                              deletingUser === membre.id ||
                                              updatingUser === membre.id
                                            }
                                          >
                                            {action === "Modifier les informations" && (
                                              <><i className="bi bi-person-gear mo-icon-blue"></i><span>Modifier</span></>
                                            )}
                                            {action === "Valider/Invalider" && (
                                              <><i className={`bi ${
                                                membre.status === 'OK' || membre.account_validation === 'OK' 
                                                  ? 'bi-x-circle mo-icon-amber' : 'bi-check-circle mo-icon-green'
                                              }`}></i>
                                              <span>{membre.status === 'OK' || membre.account_validation === 'OK' ? 'Invalider' : 'Valider'}</span></>
                                            )}
                                            {action === "Supprimer" && (
                                              <><i className="bi bi-trash mo-icon-red"></i><span>Supprimer</span></>
                                            )}
                                          </button>
                                        ))
                                      }
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5">
                                <div className="mo-empty-state">
                                  <div className="mo-empty-icon">
                                    <i className="bi bi-people"></i>
                                  </div>
                                  <h5 className="mo-empty-title">Aucun membre trouvé</h5>
                                  <p className="mo-empty-text">
                                    {searchTerm 
                                      ? `Aucun membre ne correspond à "${searchTerm}"`
                                      : "Aucun membre n'est actuellement dans votre organisation"
                                    }
                                  </p>
                                  {searchTerm && (
                                    <button className="mo-btn-empty-reset" onClick={handleClearSearch}>
                                      <i className="bi bi-arrow-clockwise me-2"></i>Afficher tous
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="mo-pagination">
                      <div className="mo-pagination-info">
                        <i className="bi bi-info-circle"></i>
                        Affichage <strong>1</strong> à <strong>{filteredMembres.length}</strong> sur <strong>{filteredMembres.length}</strong> membres
                        {searchTerm && (
                          <span className="mo-pagination-filter">• Filtré sur {membresData.length}</span>
                        )}
                      </div>
                      <div className="mo-pagination-controls">
                        <button className="mo-page-btn" disabled>
                          <i className="bi bi-chevron-left"></i>
                        </button>
                        <button className="mo-page-btn active">1</button>
                        <button className="mo-page-btn" disabled>
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* ========== MODAL ÉDITION ORGANISATION ========== */}
      <Modal show={showEditOrgModal} onHide={() => setShowEditOrgModal(false)} centered size="lg" className="mo-modal">
        <div className="mo-modal-header mo-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="mo-modal-icon">
              <i className="bi bi-building-gear"></i>
            </div>
            <div>
              <h5 className="mo-modal-title mb-0">Modifier l'entreprise</h5>
              <small className="mo-modal-subtitle">Mettez à jour les informations</small>
            </div>
          </div>
          <button className="mo-modal-close" onClick={() => setShowEditOrgModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={updateOrganization}>
          <Modal.Body className="mo-modal-body">
            <div className="mo-info-box mb-4">
              <i className="bi bi-info-circle-fill"></i>
              <span>Mettez à jour les informations de votre entreprise</span>
            </div>

            <Row className="g-3">
              <Col md={12}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-building mo-form-icon"></i>
                    Nom de l'entreprise <span className="mo-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={editOrgForm.business_name}
                    onChange={(e) => setEditOrgForm(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Entrez le nom de l'entreprise"
                    required
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-envelope mo-form-icon"></i>
                    Email <span className="mo-required">*</span>
                  </label>
                  <input
                    type="email"
                    className="mo-input"
                    value={editOrgForm.business_email}
                    onChange={(e) => setEditOrgForm(prev => ({ ...prev, business_email: e.target.value }))}
                    placeholder="contact@entreprise.com"
                    required
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-telephone mo-form-icon"></i>
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    className="mo-input"
                    value={editOrgForm.phone}
                    onChange={(e) => setEditOrgForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+241 XX XX XX XX"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-geo-alt mo-form-icon"></i>
                    Ville
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={editOrgForm.business_city}
                    onChange={(e) => setEditOrgForm(prev => ({ ...prev, business_city: e.target.value }))}
                    placeholder="Libreville"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-globe mo-form-icon"></i>
                    Pays
                  </label>
                  <select
                    className="mo-select"
                    value={editOrgForm.business_country}
                    onChange={(e) => setEditOrgForm(prev => ({ ...prev, business_country: e.target.value }))}
                  >
                    <option value="">Sélectionnez un pays</option>
                    <option value="GA">Gabon</option>
                    <option value="CG">Congo</option>
                    <option value="RDC">République démocratique du Congo</option>
                    <option value="SN">Sénégal</option>
                  </select>
                </div>
              </Col>

              <Col md={12}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-card-text mo-form-icon"></i>
                    Identifiant SIREN
                  </label>
                  <input
                    type="text"
                    className="mo-input mo-input-disabled"
                    value={editOrgForm.business_id}
                    disabled
                  />
                  <div className="mo-form-hint">
                    <i className="bi bi-lock"></i>
                    L'identifiant ne peut pas être modifié
                  </div>
                </div>
              </Col>
            </Row>
          </Modal.Body>

          <div className="mo-modal-footer">
            <button 
              type="button"
              className="mo-btn-cancel" 
              onClick={() => setShowEditOrgModal(false)}
              disabled={loading}
            >
              Annuler
            </button>
            <button 
              type="submit"
              className="mo-btn-primary" 
              disabled={loading}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" /> Mise à jour...</>
              ) : (
                <><i className="bi bi-check-lg"></i> Mettre à jour</>
              )}
            </button>
          </div>
        </Form>
      </Modal>

      {/* ========== MODAL ÉDITION MEMBRE ========== */}
      <Modal show={showEditMemberModal} onHide={() => setShowEditMemberModal(false)} centered size="lg" className="mo-modal">
        <div className="mo-modal-header mo-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="mo-modal-icon">
              <i className="bi bi-person-gear"></i>
            </div>
            <div>
              <h5 className="mo-modal-title mb-0">Modifier le membre</h5>
              <small className="mo-modal-subtitle">{selectedMember?.email}</small>
            </div>
          </div>
          <button className="mo-modal-close" onClick={() => setShowEditMemberModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={updateMember}>
          <Modal.Body className="mo-modal-body">
            {selectedMember && (
              <>
                <div className="mo-info-box mb-4">
                  <i className="bi bi-info-circle-fill"></i>
                  <span>Modification des informations pour <strong>{selectedMember.email}</strong></span>
                </div>

                <Row className="g-3">
                  <Col md={6}>
                    <div className="mo-form-group">
                      <label className="mo-form-label">
                        <i className="bi bi-person mo-form-icon"></i>Prénom
                      </label>
                      <input
                        type="text"
                        className="mo-input"
                        value={editMemberForm.first_name}
                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, first_name: e.target.value }))}
                        placeholder="Prénom"
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mo-form-group">
                      <label className="mo-form-label">
                        <i className="bi bi-person mo-form-icon"></i>Nom
                      </label>
                      <input
                        type="text"
                        className="mo-input"
                        value={editMemberForm.last_name}
                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, last_name: e.target.value }))}
                        placeholder="Nom"
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mo-form-group">
                      <label className="mo-form-label">
                        <i className="bi bi-envelope mo-form-icon"></i>Email <span className="mo-required">*</span>
                      </label>
                      <input
                        type="email"
                        className="mo-input"
                        value={editMemberForm.email}
                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="exemple@entreprise.com"
                        required
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mo-form-group">
                      <label className="mo-form-label">
                        <i className="bi bi-telephone mo-form-icon"></i>Téléphone
                      </label>
                      <input
                        type="tel"
                        className="mo-input"
                        value={editMemberForm.phone}
                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+241 XX XX XX XX"
                      />
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="mo-form-group">
                      <label className="mo-form-label">
                        <i className="bi bi-shield-check mo-form-icon"></i>Statut du compte
                      </label>
                      <select
                        className="mo-select"
                        value={editMemberForm.status}
                        onChange={(e) => setEditMemberForm(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="OK">Actif</option>
                        <option value="KO">Inactif</option>
                      </select>
                      <div className="mo-form-hint">
                        <i className="bi bi-info-circle"></i>
                        Un compte inactif ne pourra pas se connecter
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>

          <div className="mo-modal-footer">
            <button 
              type="button"
              className="mo-btn-cancel" 
              onClick={() => setShowEditMemberModal(false)}
              disabled={updatingUser}
            >
              Annuler
            </button>
            <button 
              type="submit"
              className="mo-btn-primary" 
              disabled={updatingUser}
            >
              {updatingUser ? (
                <><Spinner animation="border" size="sm" /> Mise à jour...</>
              ) : (
                <><i className="bi bi-check-lg"></i> Mettre à jour</>
              )}
            </button>
          </div>
        </Form>
      </Modal>

      {/* ========== MODAL AJOUT MEMBRE ========== */}
      <Modal show={showAddMemberModal} onHide={() => setShowAddMemberModal(false)} centered size="lg" className="mo-modal">
        <div className="mo-modal-header mo-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="mo-modal-icon">
              <i className="bi bi-person-plus-fill"></i>
            </div>
            <div>
              <h5 className="mo-modal-title mb-0">Ajouter un membre</h5>
              <small className="mo-modal-subtitle">Nouveau collaborateur</small>
            </div>
          </div>
          <button className="mo-modal-close" onClick={() => setShowAddMemberModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={addNewMember}>
          <Modal.Body className="mo-modal-body">
            <div className="mo-info-box mo-info-box-success mb-4">
              <i className="bi bi-info-circle-fill"></i>
              <span>Un email d'invitation sera envoyé au nouveau collaborateur</span>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-person mo-form-icon"></i>Prénom <span className="mo-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={addMemberForm.firstname}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, firstname: e.target.value }))}
                    placeholder="Prénom"
                    required
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-person mo-form-icon"></i>Nom <span className="mo-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={addMemberForm.lastname}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, lastname: e.target.value }))}
                    placeholder="Nom"
                    required
                  />
                </div>
              </Col>

              <Col md={12}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-envelope mo-form-icon"></i>Email <span className="mo-required">*</span>
                  </label>
                  <input
                    type="email"
                    className="mo-input"
                    value={addMemberForm.business_email}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, business_email: e.target.value }))}
                    placeholder="exemple@entreprise.com"
                    required
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-telephone mo-form-icon"></i>Téléphone
                  </label>
                  <input
                    type="tel"
                    className="mo-input"
                    value={addMemberForm.phone}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+241 XX XX XX XX"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-globe mo-form-icon"></i>Code pays
                  </label>
                  <select
                    className="mo-select"
                    value={addMemberForm.country_code}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, country_code: e.target.value }))}
                  >
                    <option value="GA">Gabon (+241)</option>
                    <option value="FR">France (+33)</option>
                    <option value="US">États-Unis (+1)</option>
                    <option value="CM">Cameroun (+237)</option>
                    <option value="SN">Sénégal (+221)</option>
                    <option value="CI">Côte d'Ivoire (+225)</option>
                  </select>
                </div>
              </Col>

              <Col md={12}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-building mo-form-icon"></i>Nom de l'entreprise
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={addMemberForm.business_name}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-geo-alt mo-form-icon"></i>Ville
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={addMemberForm.business_city}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, business_city: e.target.value }))}
                    placeholder="Libreville"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mo-form-group">
                  <label className="mo-form-label">
                    <i className="bi bi-globe mo-form-icon"></i>Pays
                  </label>
                  <input
                    type="text"
                    className="mo-input"
                    value={addMemberForm.business_country}
                    onChange={(e) => setAddMemberForm(prev => ({ ...prev, business_country: e.target.value }))}
                    placeholder="Gabon"
                  />
                </div>
              </Col>
            </Row>
          </Modal.Body>

          <div className="mo-modal-footer">
            <button 
              type="button"
              className="mo-btn-cancel" 
              onClick={() => setShowAddMemberModal(false)}
              disabled={addingMember}
            >
              Annuler
            </button>
            <button 
              type="submit"
              className="mo-btn-success" 
              disabled={addingMember}
            >
              {addingMember ? (
                <><Spinner animation="border" size="sm" /> Création...</>
              ) : (
                <><i className="bi bi-person-plus"></i> Ajouter le membre</>
              )}
            </button>
          </div>
        </Form>
      </Modal>

      {/* ========== MODAL VALIDATION ========== */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} centered className="mo-modal">
        <Modal.Body className="mo-confirm-body">
          <div className="mo-confirm-icon mo-confirm-icon-warning">
            <i className="bi bi-question-circle-fill"></i>
          </div>
          <h4 className="mo-confirm-title">Confirmer l'action</h4>
          <p className="mo-confirm-text">{validationMessage}</p>
          
          {selectedMember && (
            <div className="mo-confirm-info">
              <div className="mo-confirm-info-row">
                <span>Membre</span>
                <strong>{selectedMember.email}</strong>
              </div>
              <div className="mo-confirm-info-row">
                <span>Statut actuel</span>
                <span className={`mo-status-badge ${
                  selectedMember.status === 'OK' || selectedMember.account_validation === 'OK' 
                    ? 'mo-status-active' : 'mo-status-inactive'
                }`}>
                  {selectedMember.status === 'OK' || selectedMember.account_validation === 'OK' 
                    ? 'Activé' : 'Désactivé'}
                </span>
              </div>
            </div>
          )}

          <div className="mo-confirm-actions">
            <button 
              className="mo-btn-cancel mo-btn-flex" 
              onClick={() => setShowValidationModal(false)}
            >
              Annuler
            </button>
            <button 
              className="mo-btn-primary mo-btn-flex" 
              onClick={validateUser}
              disabled={validatingUser}
            >
              {validatingUser ? (
                <><Spinner animation="border" size="sm" /> Traitement...</>
              ) : (
                <><i className="bi bi-check-lg"></i> Confirmer</>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ========== MODAL SUPPRESSION ========== */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="mo-modal">
        <Modal.Body className="mo-confirm-body">
          <div className="mo-confirm-icon mo-confirm-icon-danger">
            <i className="bi bi-trash-fill"></i>
          </div>
          <h4 className="mo-confirm-title mo-confirm-title-danger">Attention !</h4>
          <p className="mo-confirm-text">{deleteMessage}</p>
          
          {selectedMember && (
            <div className="mo-confirm-info mo-confirm-info-danger">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>
                <strong>Action irréversible</strong>
                <p className="mb-0 small">Le membre <strong>{selectedMember.email}</strong> sera définitivement supprimé</p>
              </div>
            </div>
          )}

          <div className="mo-confirm-actions">
            <button 
              className="mo-btn-cancel mo-btn-flex" 
              onClick={() => setShowDeleteModal(false)}
            >
              Annuler
            </button>
            <button 
              className="mo-btn-danger mo-btn-flex" 
              onClick={deleteUser}
              disabled={deletingUser}
            >
              {deletingUser ? (
                <><Spinner animation="border" size="sm" /> Suppression...</>
              ) : (
                <><i className="bi bi-trash"></i> Supprimer</>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .mo-page { background: #f8fafc; min-height: 100vh; }
        .mo-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== HEADER ========== */
        .mo-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px; padding: 24px 28px;
          border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .mo-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; box-shadow: 0 8px 20px -6px rgba(16, 185, 129, 0.5);
        }
        .mo-header-title {
          font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em;
        }
        .mo-header-subtitle {
          color: #64748b; font-size: 0.875rem;
          display: flex; align-items: center; gap: 6px;
        }
        .mo-header-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 16px; background: white;
          border-radius: 20px; font-size: 0.8rem; font-weight: 700;
          color: #334155; border: 1px solid #e2e8f0;
        }
        .mo-kyc-dot {
          width: 8px; height: 8px; border-radius: 50%;
        }
        .mo-kyc-dot-ok { background: #10b981; animation: mo-pulse 2s infinite; }
        .mo-kyc-dot-pending { background: #f59e0b; animation: mo-pulse 2s infinite; }

        @keyframes mo-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }

        /* ========== SIDEBAR ========== */
        .mo-sidebar-card {
          background: white; border-radius: 16px;
          border: 1px solid #e2e8f0; overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
          position: sticky; top: 20px;
        }
        .mo-sidebar-header {
          padding: 24px 20px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-bottom: 1px solid #e2e8f0;
          display: flex; align-items: center; gap: 14px;
        }
        .mo-sidebar-avatar {
          width: 52px; height: 52px; border-radius: 14px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 20px; flex-shrink: 0;
          box-shadow: 0 6px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .mo-sidebar-info { min-width: 0; flex-grow: 1; }
        .mo-sidebar-company {
          font-weight: 700; color: #0f172a; font-size: 0.95rem;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .mo-sidebar-id {
          font-size: 0.72rem; color: #64748b;
          font-family: 'SF Mono', monospace;
        }

        .mo-sidebar-nav { padding: 12px; display: flex; flex-direction: column; gap: 4px; }
        .mo-sidebar-tab {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px; border: none; background: none;
          border-radius: 12px; cursor: pointer; text-align: left;
          transition: all 0.2s; width: 100%;
        }
        .mo-sidebar-tab:hover { background: #f8fafc; }
        .mo-sidebar-tab.active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 6px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .mo-sidebar-tab-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: #f1f5f9; color: #64748b;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
          transition: all 0.2s;
        }
        .mo-sidebar-tab.active .mo-sidebar-tab-icon {
          background: rgba(255,255,255,0.2); color: white;
        }
        .mo-sidebar-tab-content { flex-grow: 1; min-width: 0; }
        .mo-sidebar-tab-label {
          font-weight: 700; font-size: 0.875rem;
          color: #0f172a; margin-bottom: 2px;
        }
        .mo-sidebar-tab.active .mo-sidebar-tab-label { color: white; }
        .mo-sidebar-tab-desc {
          font-size: 0.72rem; color: #94a3b8;
        }
        .mo-sidebar-tab.active .mo-sidebar-tab-desc { color: rgba(255,255,255,0.85); }
        .mo-sidebar-tab-arrow { color: rgba(255,255,255,0.7); font-size: 0.8rem; }

        .mo-sidebar-verification {
          padding: 18px 20px;
          background: #f8fafc; border-top: 1px solid #e2e8f0;
        }
        .mo-sidebar-verification-header {
          display: flex; align-items: center; gap: 8px;
          font-size: 0.78rem; font-weight: 700; color: #475569;
          text-transform: uppercase; letter-spacing: 0.05em;
          margin-bottom: 10px;
        }
        .mo-sidebar-verification-header i { color: #10b981; font-size: 1rem; }
        .mo-sidebar-progress {
          height: 6px; background: #e2e8f0; border-radius: 10px;
          overflow: hidden; margin-bottom: 8px;
        }
        .mo-sidebar-progress-bar {
          height: 100%; background: linear-gradient(90deg, #f59e0b, #d97706);
          border-radius: 10px; transition: width 1s ease-out;
        }
        .mo-sidebar-progress-ok {
          background: linear-gradient(90deg, #10b981, #059669);
        }
        .mo-sidebar-progress-label {
          font-size: 0.72rem; color: #64748b; font-weight: 600;
          text-align: right;
        }

        /* ========== CONTENT CARD ========== */
        .mo-content-card {
          background: white; border-radius: 16px;
          border: 1px solid #e2e8f0; overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .mo-content-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; border-bottom: 1px solid #e2e8f0;
          background: #f8fafc; flex-wrap: wrap; gap: 12px;
        }
        .mo-content-header-icon {
          width: 48px; height: 48px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 22px; flex-shrink: 0;
        }
        .mo-content-header-icon-blue {
          background: #dbeafe; color: #3b82f6;
        }
        .mo-content-header-icon-violet {
          background: #ede9fe; color: #8b5cf6;
        }
        .mo-content-title {
          font-weight: 700; color: #0f172a; margin: 0 0 4px; font-size: 1.05rem;
        }
        .mo-content-subtitle {
          color: #64748b; font-size: 0.8rem; margin: 0;
        }
        .mo-content-body { padding: 24px; }

        /* ========== VERIFICATION CARD ========== */
        .mo-verification-card {
          background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
          border: 1px solid #e2e8f0; border-radius: 16px;
          padding: 32px 24px; text-align: center;
          height: 100%;
        }
        .mo-verification-logo {
          width: 100px; height: 100px; border-radius: 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 2rem; margin: 0 auto 20px;
          box-shadow: 0 10px 24px -8px rgba(16, 185, 129, 0.5);
        }
        .mo-verification-name {
          font-weight: 700; color: #0f172a; font-size: 1.15rem;
          margin-bottom: 16px;
        }
        .mo-verification-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 20px;
          font-size: 0.8rem; font-weight: 700; margin-bottom: 14px;
        }
        .mo-verification-badge-ok {
          background: #d1fae5; color: #059669;
        }
        .mo-verification-progress {
          height: 6px; background: #e2e8f0; border-radius: 10px;
          overflow: hidden; margin-bottom: 14px;
        }
        .mo-verification-progress-bar {
          height: 100%; border-radius: 10px; transition: width 1s ease-out;
        }
        .mo-verification-progress-ok {
          background: linear-gradient(90deg, #10b981, #059669);
        }
        .mo-verification-info {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 16px; border-radius: 10px;
          font-size: 0.8rem; font-weight: 600;
        }
        .mo-verification-info-ok {
          background: #f0fdf4; color: #059669;
          border: 1px solid #a7f3d0;
        }
        .mo-verification-info-warning {
          background: #fffbeb; color: #92400e;
          border: 1px solid #fde68a;
        }

        /* ========== DETAILS CARD ========== */
        .mo-details-card {
          background: white; border-radius: 16px;
          border: 1px solid #e2e8f0; overflow: hidden; height: 100%;
        }
        .mo-details-header {
          display: flex; align-items: center; gap: 10px;
          padding: 16px 20px; background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-weight: 700; color: #0f172a; font-size: 0.9rem;
        }
        .mo-details-header i { color: #3b82f6; font-size: 1.05rem; }
        .mo-details-body { padding: 12px 20px 20px; }
        .mo-detail-row {
          display: flex; align-items: center; gap: 14px;
          padding: 12px 0; border-bottom: 1px solid #f1f5f9;
        }
        .mo-detail-row:last-child { border-bottom: none; }
        .mo-detail-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: #eff6ff; color: #3b82f6;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0;
        }
        .mo-detail-content { flex-grow: 1; min-width: 0; }
        .mo-detail-label {
          font-size: 0.7rem; color: #94a3b8; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
        }
        .mo-detail-value {
          font-size: 0.875rem; color: #0f172a; font-weight: 600;
          word-break: break-word;
        }
        .mo-code {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          background: #f1f5f9; color: #1e40af;
          padding: 3px 8px; border-radius: 5px;
          font-size: 0.78rem; display: inline-block;
        }

        /* ========== MEMBERS STATS ========== */
        .mo-members-stats {
          display: flex; gap: 12px; flex-wrap: wrap;
          align-items: center;
        }
        .mo-member-stat {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 16px; background: #f8fafc;
          border-radius: 12px; border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .mo-member-stat-icon {
          width: 40px; height: 40px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .mo-member-stat-icon-blue { background: #dbeafe; color: #3b82f6; }
        .mo-member-stat-icon-green { background: #d1fae5; color: #10b981; }
        .mo-member-stat-label {
          font-size: 0.68rem; color: #64748b;
          text-transform: uppercase; letter-spacing: 0.05em;
          font-weight: 700; margin-bottom: 2px;
        }
        .mo-member-stat-value {
          font-size: 1.25rem; font-weight: 700; color: #0f172a;
          line-height: 1;
        }
        .mo-member-stat-value-green { color: #10b981; }

        .mo-member-stat-search {
          flex: 1; min-width: 200px; margin-left: auto;
        }
        .mo-search-wrapper { position: relative; display: flex; align-items: center; }
        .mo-search-icon {
          position: absolute; left: 14px; color: #94a3b8;
          font-size: 1.05rem; pointer-events: none;
        }
        .mo-search-input {
          width: 100%; padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; background: white;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .mo-search-input:focus {
          border-color: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        .mo-search-clear {
          position: absolute; right: 8px; background: none; border: none;
          color: #94a3b8; font-size: 1rem; padding: 6px 10px;
          cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .mo-search-clear:hover { background: #fee2e2; color: #ef4444; }

        /* ========== TABLE ========== */
        .mo-table-wrapper {
          background: white; border-radius: 14px;
          border: 1px solid #e2e8f0; overflow: hidden;
        }
        .mo-table { font-size: 0.875rem; width: 100%; }
        .mo-table thead th {
          background: #f8fafc; color: #475569; font-weight: 700;
          font-size: 0.7rem; text-transform: uppercase;
          letter-spacing: 0.05em; padding: 14px 16px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap; text-align: left;
        }
        .mo-table tbody td {
          padding: 14px 16px; vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .mo-row { transition: background-color 0.15s; }
        .mo-row:hover { background-color: #f8fafc; }

        /* Member cell */
        .mo-member-cell { display: flex; align-items: center; gap: 12px; }
        .mo-member-avatar-wrapper { position: relative; flex-shrink: 0; }
        .mo-member-avatar {
          width: 40px; height: 40px; border-radius: 12px;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 15px;
        }
        .mo-member-you-badge {
          position: absolute; top: -6px; right: -6px;
          background: #06b6d4; color: white;
          font-size: 0.6rem; font-weight: 700;
          padding: 2px 6px; border-radius: 6px;
          border: 2px solid white;
        }
        .mo-member-status-dot {
          position: absolute; bottom: -2px; right: -2px;
          width: 14px; height: 14px; border-radius: 50%;
          border: 2px solid white;
        }
        .mo-status-dot-active { background: #10b981; }
        .mo-status-dot-inactive { background: #94a3b8; }
        .mo-member-info { min-width: 0; }
        .mo-member-email {
          font-weight: 700; color: #0f172a; font-size: 0.875rem;
        }
        .mo-member-name {
          font-size: 0.78rem; color: #64748b; margin-top: 2px;
        }
        .mo-member-company {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 0.7rem; color: #94a3b8; margin-top: 2px;
        }

        /* Status badges */
        .mo-status-badges { display: flex; flex-direction: column; gap: 5px; }
        .mo-status-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          font-size: 0.72rem; font-weight: 700;
          width: fit-content;
        }
        .mo-status-active { background: #d1fae5; color: #059669; }
        .mo-status-inactive { background: #f1f5f9; color: #64748b; }
        .mo-validation-badge {
          display: inline-flex; align-items: center;
          padding: 3px 9px; border-radius: 5px;
          font-size: 0.65rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
          width: fit-content;
        }
        .mo-validation-ok { background: #d1fae5; color: #059669; }
        .mo-validation-pending { background: #fef3c7; color: #92400e; }

        /* Role badge */
        .mo-role-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 8px;
          font-size: 0.78rem; font-weight: 700;
        }
        .mo-role-admin {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          color: #1e40af;
        }
        .mo-role-user {
          background: #f1f5f9; color: #475569;
        }

        /* Date cell */
        .mo-date-cell {
          display: inline-flex; align-items: center; gap: 6px;
          color: #64748b; font-size: 0.8rem;
        }
        .mo-date-cell i { color: #94a3b8; }

        /* Actions */
        .mo-actions { display: inline-flex; gap: 6px; justify-content: flex-end; position: relative; }
        .mo-action-btn {
          width: 34px; height: 34px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white; color: #64748b;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s; font-size: 0.9rem;
        }
        .mo-action-btn:hover {
          background: #8b5cf6; border-color: #8b5cf6; color: white;
          transform: translateY(-1px);
        }
        .mo-dropdown-menu {
          border: 1px solid #e2e8f0; border-radius: 10px;
          box-shadow: 0 10px 30px -8px rgba(0,0,0,0.15);
          padding: 6px; background: white;
        }
        .mo-dropdown-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: 6px;
          font-size: 0.82rem; font-weight: 600;
          background: none; border: none; width: 100%;
          text-align: left; cursor: pointer; color: #334155;
          transition: all 0.15s;
        }
        .mo-dropdown-item:hover:not(:disabled) {
          background: #f8fafc; color: #0f172a;
        }
        .mo-dropdown-item:disabled {
          opacity: 0.5; cursor: not-allowed;
        }
        .mo-icon-blue { color: #3b82f6; }
        .mo-icon-green { color: #10b981; }
        .mo-icon-amber { color: #f59e0b; }
        .mo-icon-red { color: #ef4444; }

        /* ========== PAGINATION ========== */
        .mo-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; background: #f8fafc;
          border-top: 1px solid #e2e8f0; flex-wrap: wrap; gap: 12px;
        }
        .mo-pagination-info {
          font-size: 0.8rem; color: #64748b;
          display: flex; align-items: center; gap: 6px;
        }
        .mo-pagination-info strong { color: #0f172a; }
        .mo-pagination-filter { color: #10b981; margin-left: 6px; font-weight: 700; }
        .mo-pagination-controls { display: flex; align-items: center; gap: 6px; }
        .mo-page-btn {
          min-width: 34px; height: 34px; padding: 0 10px;
          border-radius: 8px; border: 1px solid #e2e8f0;
          background: white; color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
          font-size: 0.8rem; font-weight: 600;
        }
        .mo-page-btn:hover:not(:disabled) { background: #10b981; color: white; border-color: #10b981; }
        .mo-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .mo-page-btn.active { background: #10b981; color: white; border-color: #10b981; }

        /* ========== EMPTY / LOADING ========== */
        .mo-loading { padding: 60px 20px; text-align: center; }
        .mo-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e2e8f0;
          border-top-color: #10b981; border-radius: 50%;
          animation: mo-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .mo-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
        .mo-empty-state { padding: 50px 20px; text-align: center; }
        .mo-empty-icon {
          width: 80px; height: 80px; border-radius: 50%;
          background: #f1f5f9; color: #94a3b8;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; margin-bottom: 20px;
        }
        .mo-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; font-size: 1rem; }
        .mo-empty-text { color: #94a3b8; margin-bottom: 20px; font-size: 0.85rem; }
        .mo-btn-empty-reset {
          display: inline-flex; align-items: center;
          background: white; color: #10b981;
          border: 1px solid #a7f3d0; border-radius: 8px;
          font-weight: 600; padding: 8px 16px;
          cursor: pointer; transition: all 0.2s; font-size: 0.85rem;
        }
        .mo-btn-empty-reset:hover { background: #10b981; color: white; border-color: #10b981; }

        /* ========== BUTTONS ========== */
        .mo-btn-primary, .mo-btn-secondary, .mo-btn-success, .mo-btn-danger, .mo-btn-cancel {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 10px 18px; border-radius: 10px; font-weight: 600; font-size: 0.85rem;
          border: none; transition: all 0.2s; cursor: pointer;
        }
        .mo-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white;
        }
        .mo-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .mo-btn-secondary {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .mo-btn-secondary:hover:not(:disabled) { background: #f8fafc; }
        .mo-btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white;
        }
        .mo-btn-success:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .mo-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white;
        }
        .mo-btn-danger:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .mo-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .mo-btn-cancel:hover { background: #f1f5f9; }
        .mo-btn-primary:disabled, .mo-btn-success:disabled, .mo-btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
        .mo-btn-flex { flex: 1; }

        /* ========== ALERT ========== */
        .mo-alert {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border-radius: 12px;
          font-size: 0.85rem; font-weight: 500;
        }
        .mo-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .mo-alert-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); color: #065f46; border-left: 4px solid #10b981; }
        .mo-alert-danger { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); color: #991b1b; border-left: 4px solid #ef4444; }
        .mo-alert-info { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-left: 4px solid #3b82f6; }

        /* ========== INFO BOX ========== */
        .mo-info-box {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 16px; border-radius: 10px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-left: 4px solid #3b82f6;
          color: #1e40af; font-size: 0.82rem;
        }
        .mo-info-box-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-left-color: #10b981; color: #065f46;
        }
        .mo-info-box i { font-size: 1.05rem; flex-shrink: 0; }

        /* ========== TOAST ========== */
        .mo-toast { border-radius: 12px; overflow: hidden; box-shadow: 0 10px 30px -8px rgba(0,0,0,0.15); }
        .mo-toast .toast-header {
          border: none; padding: 12px 16px; background: white;
          border-bottom: 1px solid #f1f5f9;
        }
        .mo-toast .toast-body {
          padding: 14px 16px; font-size: 0.85rem; font-weight: 500;
        }
        .mo-toast-success .toast-body { background: #f0fdf4; color: #065f46; }
        .mo-toast-error .toast-body { background: #fef2f2; color: #991b1b; }
        .mo-toast-icon {
          width: 28px; height: 28px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; margin-right: 8px;
        }
        .mo-toast-icon-success { background: #d1fae5; color: #059669; }
        .mo-toast-icon-error { background: #fee2e2; color: #dc2626; }

        /* ========== MODAL ========== */
        .mo-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .mo-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .mo-modal-header-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .mo-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .mo-modal-icon {
          width: 44px; height: 44px; border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
        }
        .mo-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .mo-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .mo-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .mo-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .mo-modal-body {
          padding: 24px; background: #f8fafc;
          max-height: 70vh; overflow-y: auto;
        }
        .mo-modal-footer {
          padding: 16px 24px; background: white;
          border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* ========== FORM ========== */
        .mo-form-group { margin-bottom: 4px; }
        .mo-form-label {
          display: flex; align-items: center; gap: 6px;
          font-weight: 700; color: #0f172a;
          font-size: 0.82rem; margin-bottom: 8px;
        }
        .mo-form-icon { color: #3b82f6; font-size: 0.9rem; }
        .mo-required { color: #ef4444; }
        .mo-input, .mo-select {
          width: 100%; padding: 11px 14px;
          border: 2px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; color: #0f172a; background: white;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .mo-input:focus, .mo-select:focus {
          border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .mo-input-disabled {
          background: #f8fafc; color: #64748b; cursor: not-allowed;
        }
        .mo-form-hint {
          margin-top: 6px; font-size: 0.72rem; color: #94a3b8;
          display: flex; align-items: center; gap: 4px;
        }
        .mo-form-hint i { color: #3b82f6; }

        /* ========== CONFIRM MODAL ========== */
        .mo-confirm-body { padding: 40px 32px 32px; text-align: center; }
        .mo-confirm-icon {
          width: 88px; height: 88px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 44px; margin-bottom: 20px;
        }
        .mo-confirm-icon-warning {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          color: #d97706;
          box-shadow: 0 12px 30px -10px rgba(245, 158, 11, 0.4);
        }
        .mo-confirm-icon-danger {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          box-shadow: 0 12px 30px -10px rgba(220, 38, 38, 0.4);
        }
        .mo-confirm-title {
          font-weight: 700; color: #0f172a;
          font-size: 1.25rem; margin-bottom: 12px;
        }
        .mo-confirm-title-danger { color: #dc2626; }
        .mo-confirm-text {
          color: #64748b; font-size: 0.875rem; margin-bottom: 20px;
          line-height: 1.5;
        }
        .mo-confirm-info {
          background: #f1f5f9; border-radius: 12px;
          padding: 16px 20px; margin-bottom: 20px;
          text-align: left;
        }
        .mo-confirm-info-danger {
          display: flex; align-items: flex-start; gap: 12px;
          background: #fef2f2; color: #991b1b;
          padding: 14px 18px;
        }
        .mo-confirm-info-danger i { color: #dc2626; font-size: 1.3rem; flex-shrink: 0; margin-top: 2px; }
        .mo-confirm-info-row {
          display: flex; justify-content: space-between;
          align-items: center; padding: 6px 0;
          font-size: 0.85rem; color: #64748b;
        }
        .mo-confirm-info-row strong { color: #0f172a; font-weight: 700; }
        .mo-confirm-actions { display: flex; gap: 10px; }

        /* ========== ANIMATIONS ========== */
        .mo-spin { animation: mo-spin 1s linear infinite; display: inline-block; }
        @keyframes mo-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .mo-sidebar-card { position: static; }
        }
        @media (max-width: 768px) {
          .mo-header { padding: 20px; }
          .mo-header-title { font-size: 1.2rem; }
          .mo-content-body { padding: 16px; }
          .mo-members-stats { flex-direction: column; align-items: stretch; }
          .mo-member-stat-search { margin-left: 0; }
          .mo-pagination { flex-direction: column; }
          .mo-confirm-actions { flex-direction: column; }
        }
      `}</style>
    </div>
  );






}